import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getBrand } from "../branding";

const brand = getBrand();

export default function ExamLogin() {
  const [tmtaId, setTmtaId] = useState("");
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [session, setSession] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  const navigate = useNavigate();

  /* ───────── CHECK EXISTING SESSION ───────── */
  useEffect(() => {
    fetch("/api/session", { credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data?.active) setSession(data);
      })
      .catch(() => {})
      .finally(() => setCheckingSession(false));
  }, []);

  /* ───────── LOGIN (CREATES SESSION) ───────── */
  async function handleLogin(e) {
    e.preventDefault();
    if (checkingSession || loading) return;
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tmtaId: tmtaId.trim(),
          passcode
        })
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 401)
          throw new Error("Invalid registration ID or passcode");
        if (res.status === 403)
          throw new Error(data?.error || "Access not permitted");
        throw new Error("Login failed");
      }

      // 🔑 unify flow: login returns SAME shape as /api/session
      setSession(data);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  /* ───────── CONTINUE TO EXAM ───────── */
  function continueToExam() {
    navigate(`/exam/${session.examId}`);
  }

  /* ───────── LOG OUT ───────── */
  async function logout() {
    await fetch("/api/session/logout", {
      method: "POST",
      credentials: "include"
    });
    setSession(null);
    setTmtaId("");
    setPasscode("");
  }

  return (
    <div style={styles.wrap}>
      <form
        onSubmit={session ? continueToExam : handleLogin}
        style={styles.card}
        aria-busy={checkingSession || loading}
      >
        <Header />

        {!session && (
          <>
            <div style={styles.field}>
              <label style={styles.label}>Registration ID</label>
              <input
                value={tmtaId}
                onChange={e => setTmtaId(e.target.value)}
                disabled={checkingSession || loading}
                required
                //placeholder="e.g. theory_180001"
                autoComplete="off"
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Passcode</label>
              <input
                type="password"
                //placeholder="12345678"
                value={passcode}
                onChange={e => setPasscode(e.target.value)}
                disabled={checkingSession || loading}
                required
                style={styles.input}
              />
            </div>
          </>
        )}

        {session && (
          <div style={styles.confirmBox}>
            <div style={{ marginTop: 6, marginBottom: 18, fontWeight: 600, fontSize: "clamp(20px, 5vw, 28px)", lineHeight: 1.25 }}>
              {session.examType[0].toUpperCase() +
                session.examType.slice(1)}{" "}
              – {session.examLevel}
            </div>

            <div style={{ fontSize: 14, marginBottom: 14 }}>
              <div style={{ marginBottom: 6 }}>Logged in as</div>
              <code style={{ display: "block", fontSize: "clamp(14px, 4vw, 18px)", lineHeight: 1.5, overflowWrap: "anywhere" }}>{session.tmtaId}</code>
            </div>

            <span style={styles.warning}>
              If this is <strong>NOT</strong> your ID, please log out immediately.
            </span>
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}

        <button
          disabled={loading || checkingSession}
          style={{
            ...styles.primaryButton,
            opacity: loading || checkingSession ? 0.7 : 1
          }}
        >

          {loading ? "Checking…" : session ? "Begin Exam" : "Continue"}
        </button>

        {session && (
          <button
            type="button"
            onClick={logout}
            style={styles.secondaryButton}
          >
            Log out and start over
          </button>
        )}

        {brand.shortName === "FSMTA" && <a href="/teachers" style={{ textAlign: "center", fontSize: 14 }}>Teacher login and registration</a>}
        <Footer />
      </form>
    </div>
  );
}

/* ───────── HEADER ───────── */
// function Header() {
//   return (
//     <div style={styles.header}>
//       <img
//         src="/tmta-logo.png"
//         alt="Texas Music Teachers Association"
//         style={styles.logo}
//       />
//       <h2 style={styles.title}>Texas Music Teachers Association</h2>
//       <div style={styles.examSubtitle}>
//         Online Theory &amp; World of Music Exams
//       </div>
//     </div>
//   );
// }
function Header() {
  return (
    <div style={styles.header}>
      <img
        onError={e => { e.currentTarget.style.display = "none"; }}
        src={brand.logo}
        alt={brand.organizationName}
        style={styles.logo}
      />

      <h2 style={styles.title}>
        {brand.organizationName}
      </h2>

      <div style={styles.examSubtitle}>
        {brand.examSubtitle}
      </div>
    </div>
  );
}

/* ───────── FOOTER ───────── */
// function Footer() {
//   return (
//     <div style={styles.footer}>
//       <div>© Texas Music Teachers Association</div>
//       <div>
//         Powered by <strong>Lion Theory</strong>
//       </div>
//     </div>
//   );
// }
function Footer() {
  return (
    <div style={styles.footer}>
      <div>{brand.footer}</div>

      <div>
        Powered by <strong>Lion Theory</strong>
      </div>
    </div>
  );
}

/* ───────── STYLES ───────── */
const styles = {
  wrap: {
    minHeight: "100vh",
    width: "100%",
    boxSizing: "border-box",
    padding: "24px 16px",
    colorScheme: "light",
    fontFamily: "system-ui, sans-serif",
    display: "grid",
    placeItems: "center",
    gridTemplateColumns: "minmax(0, 1fr)",
    background: "#fffaf5"
  },
  card: {
    width: "100%",
    maxWidth: 450,
    minWidth: 0,
    gridTemplateColumns: "minmax(0, 1fr)",
    minHeight: 510,
    alignContent: "start",
    boxSizing: "border-box",
    color: "#1f1f1f",
    padding: 24,
    borderRadius: 20,
    background: "#fff",
    boxShadow: "0 6px 18px rgba(0,0,0,.08)",
    display: "grid",
    gap: 14
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 6,
    marginBottom: 12
  },
  logo: {
    maxWidth: 140,
    aspectRatio: "1 / 1",
    width: "100%",
    height: "auto",
    objectFit: "contain"
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600
  },
  examSubtitle: {
    fontSize: 15,
    fontWeight: 500,
    color: "#444"
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  label: {
    fontSize: 13,
    fontWeight: 500
  },
  input: {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    padding: "11px 12px",
    fontSize: 16,
    borderRadius: 6,
    border: "1px solid #ccc"
  },
  confirmBox: {
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    overflowWrap: "anywhere",
    padding: 14,
    borderRadius: 8,
    background: "#eef3f8",
    textAlign: "center",
    border: "1px solid #dbe5ef"
  },
  warning: {
    display: "block",
    marginTop: 10,
    fontSize: 12,
    background: "#fdecea",
    border: "1px solid #f5c2c7",
    padding: "5px 7px",
    borderRadius: 6,
    color: "#8a1f11"
  },
  error: {
    color: "#b00020",
    fontSize: 14
  },
  footer: {
    marginTop: 18,
    paddingTop: 14,
    borderTop: "1px solid #eee",
    fontSize: 12,
    color: "#777",
    textAlign: "center"
  },
  secondaryButton: {
    background: "transparent",
    border: "none",
    color: "#666",
    cursor: "pointer"
  },
  primaryButton: {
    padding: "14px 16px",
    fontSize: 18,
    fontWeight: 600,
    borderRadius: 8,
    border: "none",
    background: "#f28c38",
    color: "#281709",
    cursor: "pointer",
    transition: "background 0.15s ease"
  }

};
